import React, { useMemo, useState, forwardRef, useImperativeHandle, useRef } from 'react'
import { Modal, Form as AntForm, message as $message } from 'antd'
import { Input } from '@/ui'

import { buildFields } from '@/utils/form'

import ImgCode from '@/components/FormWidgets/ImgCode'
import MsgCode from '@/components/FormWidgets/MsgCode'

import { sendCheckCode } from '@/services/supplier/supplierContact'
import { updateSelfPhone } from '@/services/user'

import styles from './PerfectAdminPhone.less'

import { createAPI } from '@/utils/modal'

const fieldsMap = [
    {
        name: 'imgCode',
        label: '图形验证码',
        component: ImgCode,
        options: {
            validateFirst: true,
            rules: [
                {
                    required: true,
                    message: '请输入图形验证码',
                },
                {
                    validator(rule, value) {
                        if (value && value.checkCode) {
                            return Promise.resolve()
                        }

                        return Promise.reject(new Error('请输入图形验证码'))
                    },
                },
            ],
        },
        props: {
            style: {
                width: '200px',
            },
            maxLength: 4,
            placeholder: '请输入图形验证码',
        },
    },
    {
        name: 'mobile',
        label: '手机号',
        component: MsgCode,
        options: {
            rules: [
                {
                    required: true,
                    message: '请输入手机号',
                },
            ],
        },
        props: ({ form, form: { validateFields } }) => ({
            style: {
                width: '200px',
            },
            placeholder: '请输入手机号',
            fetch: async () => {
                const { mobile, imgCode } = await validateFields(['mobile', 'imgCode'])

                const { success, message } = await sendCheckCode({
                    phone: mobile,
                    // 花聪要这里写死
                    phoneCheckTypeEnum: 'MODIFY_CHANNEL_MANAGER',
                    ...(imgCode || {}),
                })

                if (success) {
                    return
                } else {
                    $message.error(message)
                }

                form.reloadImgCode()

                return Promise.reject(new Error('获取验证码失败'))
            },
        }),
    },
    {
        name: 'smsCode',
        label: '验证码',
        component: Input,
        options: {
            rules: [
                {
                    required: true,
                    message: '请输入验证码',
                },
            ],
        },
        props: {
            style: {
                width: '120px',
            },
            maxLength: 6,
            placeholder: '请输入验证码',
        },
    },
]

const createFields = buildFields({
    fieldsList: ['imgCode', 'mobile', 'smsCode'],
    fieldsMap,
})

const formItemLayout = {
    labelCol: {
        span: 4,
    },
    wrapperCol: {
        span: 18,
    },
}

const TopTip = () => {
    return <div className={styles.topTip}>您好，您还未设置手机号，请设置</div>
}

const PerfectAdminPhone = forwardRef(({ beforeFn, initialValues }, ref) => {
    const [form] = AntForm.useForm()

    const fields = useMemo(() => {
        return createFields({
            form,
        })
    }, [form])

    useImperativeHandle(ref, () => form)

    return (
        <>
            <TopTip />
            <AntForm form={form} initialValues={initialValues} style={{ paddingTop: '15px' }}>
                {fields.map(item => (
                    <AntForm.Item
                        key={item.name}
                        name={item.name}
                        required={false}
                        label={item.label}
                        {...formItemLayout}
                        {...item.options}
                    >
                        <item.component form={form} {...item.props} />
                    </AntForm.Item>
                ))}
            </AntForm>
        </>
    )
})

const PerfectAdminPhoneModal = ({ visible, initialValues, afterClose, onOk, onCancel }) => {
    const [loading, setLoading] = useState(false)

    const $form = useRef(null)

    const handleOk = async () => {
        const { imgCode, mobile: phone, ...rest } = await $form.current.validateFields()

        const transValues = {
            phone,
            ...rest,
        }

        setLoading(true)

        try {
            const { success, message } = await updateSelfPhone(transValues)

            if (success) {
                $message.success('设置成功，请继续操作')

                onOk && onOk(onCancel)
            } else {
                $form.current.resetFields(['imgCode', 'smsCode'])

                $message.error(message)
            }
        } catch (e) {
            console.error('catchError', e)
        }

        setLoading(false)
    }

    return (
        <Modal
            confirmLoading={loading}
            width="580px"
            title="提示"
            okText="确定"
            cancelText="取消"
            visible={visible}
            bodyStyle={{ padding: '15px 24px' }}
            afterClose={afterClose}
            onOk={handleOk}
            onCancel={onCancel}
        >
            <PerfectAdminPhone ref={$form} initialValues={initialValues} />
        </Modal>
    )
}

PerfectAdminPhone.show = async config => {
    const { initialValues } = config || {}

    if (PerfectAdminPhone.isLock) {
        return
    }

    PerfectAdminPhone.isLock = true

    return createAPI(PerfectAdminPhoneModal, {
        initialValues,
        afterClose() {
            PerfectAdminPhone.isLock = false
        },
        onOk(close) {
            close()
        },
    })
}

export default PerfectAdminPhone
